// src/oura/components/newscan/GscCard.tsx
//
// The optional Search Console card on the Website step. It reads
// and drives the flow's Search Console state (useScanFlow().gsc), so it shows
// the same truth wherever it is placed.
//
// States:
// - no site yet: Connect is off, with "Paste the website first."
// - not connected: what connecting adds, who has to give access, Connect.
// - connecting: the pill spins. Live opens Google's consent in a new tab and
//   the flow re-checks on focus and every 5 s; "Check again" asks at once.
// - setup unfinished (`action_needed`): an amber tag and "Finish connecting".
// - connected: a green tag, the property, the card tinted green. No
//   Disconnect here: the business's Connectors tab owns that.
//
// Optional means optional: nothing on the step waits for this card.

import { SearchIcon } from "lucide-react";
import { useEffect, useId, useRef } from "react";
import { cn } from "@oura/lib/cn";
import { IconSquare, PillButton, SURFACE, TYPE, Tag } from "@oura/ui";
import { Muted } from "@oura/components/newscan/Muted";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import { scanDraftMode } from "@oura/newscan/flags";

const GSC_COPY = {
    title: "Google Search Console",
    optional: "Optional",
    connected: "Connected",
    unfinished: "Setup unfinished",
    line: "Connect it for richer questions. The draft starts from the searches people already make to find this site, and every question shows how often it is searched.",
    connectedLine: (site: string) =>
        `Reading 12 months of searches for ${site}. The draft uses them, and every question shows how often it is searched.`,
    unfinishedLine: "The connection was started but never finished, so no searches are read yet.",
    fine: "Needs the client's Search Console access. Most clients connect it at onboarding; you can also add it later.",
    property: (p: string) => `Property: ${p}`,
    connect: "Connect",
    finish: "Finish connecting",
    connecting: "Connecting…",
    checkAgain: "Check again",
    needsSite: "Paste the website first.",
    saidConnected: "Search Console connected.",
} as const;

export interface GscCardProps {
    className?: string;
}

export function GscCard({ className }: GscCardProps) {
    const flow = useScanFlow();
    const { gsc } = flow;
    const site = flow.ctl.draft?.domain ?? flow.site.normalized;
    const titleId = useId();
    const reasonId = useId();

    const live = scanDraftMode() === "live";
    const connected = gsc.status === "connected";

    const connect = () => void flow.connectGsc();

    const connecting = gsc.status === "connecting";
    const unfinished = gsc.status === "action_needed";
    const noSite = !site;

    // The Connect pill is disabled while it spins and gone once connected, so the focus it
    // held drops to the page: hand it to the card's title, unless the staffer moved on.
    const titleRef = useRef<HTMLHeadingElement>(null);
    const wasConnecting = useRef(connecting);
    useEffect(() => {
        const was = wasConnecting.current;
        wasConnecting.current = connecting;
        if (!was || connecting) return;
        const raf = requestAnimationFrame(() => {
            const active = document.activeElement;
            if (!active || active === document.body) titleRef.current?.focus({ preventScroll: true });
        });
        return () => cancelAnimationFrame(raf);
    }, [connecting]);

    return (
        <section
            aria-labelledby={titleId}
            className={cn(
                connected ? "rounded-2xl bg-emerald-50/60 p-4 ring-1 ring-inset ring-emerald-100" : SURFACE.block,
                "flex flex-wrap items-start gap-x-3 gap-y-3 motion-safe:transition-colors motion-safe:duration-300",
                className,
            )}
        >
            <IconSquare tone={connected ? "green" : "blue"} icon={SearchIcon} />
            <div className="min-w-0 flex-1 basis-56">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-1">
                    <h2 ref={titleRef} id={titleId} tabIndex={-1} className={cn(TYPE.tb, "m-0 font-semibold text-gray-900 outline-none")}>
                        {GSC_COPY.title}
                    </h2>
                    {connected ? (
                        <Tag tone="green">{GSC_COPY.connected}</Tag>
                    ) : unfinished ? (
                        <Tag tone="amber">{GSC_COPY.unfinished}</Tag>
                    ) : null}
                </div>
                <p className={cn(TYPE.tc, "m-0 mt-1 break-words text-gray-600")}>
                    {connected ? GSC_COPY.connectedLine(site) : unfinished ? GSC_COPY.unfinishedLine : `${GSC_COPY.optional}. ${GSC_COPY.line}`}
                </p>
                {connected ? (
                    gsc.property && (
                        <Muted as="p" tone="ink4" size="tm" className="mt-1.5 break-all">
                            {GSC_COPY.property(gsc.property)}
                        </Muted>
                    )
                ) : (
                    <Muted as="p" tone="ink4" size="tm" className="mt-1.5">
                        {GSC_COPY.fine}
                    </Muted>
                )}
                {gsc.error && !connected && (
                    <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 break-words font-medium text-rose-600")}>
                        {gsc.error}
                    </p>
                )}
                <span role="status" className="sr-only">
                    {connected ? GSC_COPY.saidConnected : ""}
                </span>
            </div>
            {!connected && (
                <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 max-sm:basis-full max-sm:pl-11 sm:flex-col sm:items-end">
                    <PillButton
                        size="sm"
                        busy={connecting}
                        disabled={noSite}
                        aria-describedby={noSite ? reasonId : undefined}
                        onClick={connect}
                        className="max-sm:h-9"
                    >
                        {connecting ? GSC_COPY.connecting : unfinished ? GSC_COPY.finish : GSC_COPY.connect}
                    </PillButton>
                    {noSite && (
                        <Muted id={reasonId} size="tm">
                            {GSC_COPY.needsSite}
                        </Muted>
                    )}
                    {gsc.status === "connecting" && live && (
                        <PillButton variant="text" onClick={() => void flow.recheckGsc()}>
                            {GSC_COPY.checkAgain}
                        </PillButton>
                    )}
                </div>
            )}
        </section>
    );
}

export default GscCard;
